import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../services/api.service';
import {
  appendMmsCouponImageToFormData,
  extractCampaignLogoUploadUrl,
  MMS_COUPON_IMAGE_FIELD,
} from '../../optional-setting/campaign-logo-upload.util';

declare let SegmentedMessage: any;

@Component({
  selector: 'app-create-text-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './create-text-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-text-modal.component.scss',
})
export class CreateTextModalComponent implements OnInit, OnChanges, AfterViewChecked {
  @ViewChild('textAreaRef', { static: false }) textAreaRef!: ElementRef<HTMLElement>;

  private isUpdatingContent = false;
  private lastTextMessage = '';
  private savedCursorPosition = -1;

  private apiService = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);

  constructor() {}
  @Input() isOpen = false;
  @Input() initialData: any = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();

  // Character limits
  maxSenderIdLength = 50;
  maxTextMessageLength = 1000;
  maxLinkTextLength = 100;
  maxTextSegments = 3;

  // Form data
  senderId = '';
  textMessage = '';
  linkText = '';

  // Validation states
  senderIdError = '';
  textMessageError = '';
  textMessageSegmentError = '';
  linkTextError = '';

  // Touched states for blur validation
  senderIdTouched = false;
  textMessageTouched = false;
  linkTextTouched = false;

  // Available tags for insertion
  availableTags = [
    { name: 'Offer_Name', display: 'Offer_Name' },
    { name: 'Brand_Name', display: 'Brand_Name' },
    { name: 'Last_Visit_Date', display: 'Last_Visit_Date' },
  ];

  // =====================
  // MMS state
  // =====================
  messageType: 'SMS' | 'MMS' = 'SMS';
  splitTwoBubbles = true;

  mmsAltText = '';
  mmsCaption = '';
  mmsUrl = '';
  mmsUrlValid = false;
  mmsUrlTouched = false;
  mmsUrlError = '';
  mmsFallbackBehavior = 'send-sms-with-image-link';
  mmsFallbackText = '';
  mmsBubble2Text = '';
  mmsImageFile: File | null = null;
  mmsImagePreviewUrl: string | null = null;
  mmsImageUrl = '';
  isMmsImageUploading = false;
  mmsImageWidth = 0;
  mmsImageHeight = 0;

  // MMS validation states
  maxMmsSegments = 2;
  mmsImageTouched = false;
  mmsImageError = '';
  mmsFallbackTextTouched = false;
  mmsFallbackTextError = '';
  mmsFallbackSegmentError = '';
  mmsCaptionSegmentError = '';
  mmsBubble2SegmentError = '';

  mmsCaptionTags = [
    { name: 'offer_name', display: 'offer_name' },
    { name: 'brand_name', display: 'brand_name' },
  ];

  mmsFallbackTags = [{ name: 'redemption_link', display: 'redemption_link' }];

  mmsBubble2Tags = [
    { name: 'Last_Visit_Date', display: 'Last_Visit_Date' },
    { name: 'offer_name', display: 'offer_name' },
    { name: 'brand_name', display: 'brand_name' },
  ];

  ngOnInit(): void {
    // Populate form fields if initial data is provided
    this.populateFormFields();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When modal opens or initialData changes, populate the form fields
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      this.populateFormFields();
    }
    if (changes['initialData']) {
      this.populateFormFields();
    }
  }

  /**
   * Populate form fields from this.initialData when available or reset all fields and validation states when not; also update formatted content and manage cursor/textarea state.
   * @example
   * opulateFormFields()
   * void
   * @returns {{void}} No return value; updates component state and DOM asynchronously.
   */
  populateFormFields(): void {
    if (this.initialData) {
      if (this.initialData.messageType === 'MMS') {
        this.messageType = 'MMS';
        this.mmsAltText = this.initialData.mmsAltText || '';
        this.mmsCaption = this.initialData.mmsCaption || '';
        this.mmsUrl = this.initialData.mmsUrl || '';
        this.mmsFallbackBehavior =
          this.initialData.mmsFallbackBehavior || 'send-sms-with-image-link';
        this.mmsFallbackText = this.initialData.mmsFallbackText || '';
        this.mmsBubble2Text = this.initialData.mmsBubble2Text || '';
        this.splitTwoBubbles = this.initialData.splitTwoBubbles !== false;
        this.mmsImageUrl = this.initialData.mmsImageUrl || '';
        this.mmsImageWidth = this.initialData.mmsImageWidth || 0;
        this.mmsImageHeight = this.initialData.mmsImageHeight || 0;
        // Show the committed image URL as preview if no local blob
        this.mmsImagePreviewUrl = this.mmsImageUrl || null;
        this.mmsImageFile = null;
        this.isMmsImageUploading = false;
        // Reset MMS validation states
        this.mmsImageTouched = false;
        this.mmsImageError = '';
        this.mmsFallbackTextTouched = false;
        this.mmsFallbackTextError = '';
        this.mmsFallbackSegmentError = '';
        this.mmsCaptionSegmentError = '';
        this.mmsBubble2SegmentError = '';
        this.mmsUrlTouched = false;
        this.mmsUrlError = '';
        this.mmsUrlValid = this.mmsUrl ? this.isValidUrl(this.mmsUrl) : false;
      } else {
        this.messageType = 'SMS';
        this.senderId = this.initialData.senderId || '';
        this.textMessage = this.initialData.textMessage || '';
        this.linkText = this.initialData.linkText || '';
        // Reset SMS validation states when populating
        this.senderIdTouched = false;
        this.textMessageTouched = false;
        this.linkTextTouched = false;
        this.senderIdError = '';
        this.textMessageError = '';
        this.textMessageSegmentError = '';
        this.linkTextError = '';
        // Update formatted content after populating
        setTimeout(() => {
          this.updateFormattedContent();
          this.lastTextMessage = this.textMessage;
          this.savedCursorPosition = this.textMessage.length;
        }, 0);
        this.resetMmsFields();
      }
    } else {
      // Reset form fields if no initial data
      this.messageType = 'SMS';
      this.senderId = '';
      this.textMessage = '';
      this.linkText = '';
      this.senderIdTouched = false;
      this.textMessageTouched = false;
      this.linkTextTouched = false;
      this.senderIdError = '';
      this.textMessageError = '';
      this.textMessageSegmentError = '';
      this.linkTextError = '';
      this.lastTextMessage = '';
      this.savedCursorPosition = -1;
      this.resetMmsFields();

      // Clear formatted content
      setTimeout(() => {
        if (this.textAreaRef) {
          this.textAreaRef.nativeElement.innerHTML = '';
        }
      }, 0);
    }
  }

  private resetMmsFields(): void {
    this.mmsAltText = '';
    this.mmsCaption = '';
    this.mmsUrl = '';
    this.mmsUrlValid = false;
    this.mmsUrlTouched = false;
    this.mmsUrlError = '';
    this.mmsFallbackBehavior = 'send-sms-with-image-link';
    this.mmsFallbackText = '';
    this.mmsBubble2Text = '';
    this.splitTwoBubbles = true;
    this.mmsImageFile = null;
    this.mmsImagePreviewUrl = null;
    this.mmsImageUrl = '';
    this.isMmsImageUploading = false;
    this.mmsImageWidth = 0;
    this.mmsImageHeight = 0;
    this.mmsImageTouched = false;
    this.mmsImageError = '';
    this.mmsFallbackTextTouched = false;
    this.mmsFallbackTextError = '';
    this.mmsFallbackSegmentError = '';
    this.mmsCaptionSegmentError = '';
    this.mmsBubble2SegmentError = '';
  }

  onClose(): void {
    this.close.emit();
  }

  /**
   * Validate all form fields and, if valid, emit a save event with the campaign data (senderId, textMessage, linkText).
   * @example
   * nSave()
   * // emits this.save event with { senderId, textMessage, linkText } when the form is valid
   * @returns {{void}} Returns nothing; emits a save event when the form passes validation.
   **/
  onSave(): void {
    if (this.messageType === 'MMS') {
      this.validateAllMmsFields();
      if (this.isMmsFormValid()) {
        const campaignData = {
          messageType: 'MMS',
          platform: 'mms_campaign',
          mms_config: {
            message_type: 'mms',
            mms_fallback: {
              text: this.mmsFallbackText,
              behavior: this.mmsFallbackBehavior.replace(/-/g, '_'),
            },
            mms_link: this.mmsUrl || '',
            bubble_1_coupon: {
              image_width: this.mmsImageWidth || 640,
              image_height: this.mmsImageHeight || 640,
              image_caption: this.mmsCaption,
              image_alt_text: this.mmsAltText,
              coupon_image_url: this.mmsImageUrl,
            },
            bubble_2_text_body: this.splitTwoBubbles ? this.mmsBubble2Text : '',
          },
          splitTwoBubbles: this.splitTwoBubbles,
        };
        this.save.emit(campaignData);
      }
    } else {
      this.validateAllFields();
      if (this.isFormValid()) {
        const campaignData = {
          messageType: 'SMS',
          senderId: this.senderId,
          textMessage: this.textMessage,
          linkText: this.linkText,
        };
        this.save.emit(campaignData);
      }
    }
  }

  /**
   * Insert a tag into the component's textMessage at the current selection or saved cursor position, enforce maximum message length, update formatted content, and reposition the cursor.
   * @example
   * insertTag('@firstName')
   * undefined
   * @param {{string}} {{tag}} - Tag name to insert; the method wraps it in double curly braces and appends a space before inserting.
   * @returns {{void}} Updates internal state and DOM (no return value).
   **/
  insertTag(tag: string): void {
    if (!this.textAreaRef) return;

    const element = this.textAreaRef.nativeElement;

    // Try to get cursor position from current selection first
    let cursorPosition: number | null = null;
    const selection = window.getSelection();

    // If we have a valid selection within our element, use it
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      // Check if range is within our textarea element
      if (
        element.contains(range.commonAncestorContainer) ||
        element === range.commonAncestorContainer
      ) {
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        cursorPosition = preCaretRange.toString().length;
      }
    }

    // If we couldn't get cursor position from selection, use saved position
    if (cursorPosition === null) {
      if (this.savedCursorPosition >= 0) {
        cursorPosition = this.savedCursorPosition;
      } else {
        // No saved position, default to end of text
        cursorPosition = this.textMessage.length;
      }
    }

    // Insert tag with space at cursor position
    const tagText = `{{${tag}}} `;
    const beforeCursor = this.textMessage.substring(0, cursorPosition);
    const afterCursor = this.textMessage.substring(cursorPosition);
    const newMessage = beforeCursor + tagText + afterCursor;

    // Check if inserting tag would exceed character limit
    if (newMessage.length > this.maxTextMessageLength) {
      // Calculate how much of the tag can fit
      const remainingChars = this.maxTextMessageLength - (beforeCursor.length + afterCursor.length);
      if (remainingChars <= 0) {
        // No space for tag, don't insert
        return;
      }
      // Truncate tag if necessary
      const truncatedTag = tagText.substring(0, remainingChars);
      this.textMessage = beforeCursor + truncatedTag + afterCursor;
    } else {
      this.textMessage = newMessage;
    }
    this.lastTextMessage = this.textMessage;

    // Calculate new cursor position (after tag and space)
    const newCursorPosition = cursorPosition + tagText.length;
    this.savedCursorPosition = newCursorPosition;

    // Update formatted content without restoring old cursor position
    this.applyTextFormatting(element);

    // Set cursor position after formatting
    setTimeout(() => {
      if (this.textAreaRef) {
        element.focus();
        this.setCursorPosition(newCursorPosition);
        this.isUpdatingContent = false;
      }
    }, 0);
  }

  /**
   * Set the caret/selection at a character position within an element. Shared by setCursorPosition and restoreCursorPosition.
   */
  private setSelectionAtPosition(element: HTMLElement, position: number): void {
    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    let charCount = 0;
    const nodeStack: Node[] = [element];
    let node: Node | undefined;
    let foundStart = false;

    while (!foundStart && (node = nodeStack.pop())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const nextCharCount = charCount + (node.textContent?.length || 0);
        if (position <= nextCharCount) {
          range.setStart(node, position - charCount);
          foundStart = true;
        } else {
          charCount = nextCharCount;
        }
      } else {
        for (let i = node.childNodes.length - 1; i >= 0; i -= 1) {
          nodeStack.push(node.childNodes[i] as Node);
        }
      }
    }

    if (foundStart) {
      range.setEnd(range.startContainer, range.startOffset);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      range.selectNodeContents(element);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  private setCursorPosition(position: number): void {
    if (!this.textAreaRef) return;
    this.setSelectionAtPosition(this.textAreaRef.nativeElement, position);
    // Update the formatted content after inserting tag
    this.updateFormattedContent();
  }

  /**
   * Returns the message preview text for the campaign editor, using this.textMessage if available or a default placeholder otherwise.
   * @example
   * etPreviewMessage()
   * "Write the text of your message here. Describe the offers in this campaign and why it can't be missed."
   * @returns {string} Preview text for the message.
   */
  getPreviewMessage(): string {
    const preview =
      this.textMessage ||
      "Write the text of your message here. Describe the offers in this campaign and why it can't be missed.";

    // Replace tags with sample data for preview
    // preview = preview.replace(/{Customer_Name}/g, 'John Doe');
    // preview = preview.replace(/{Customer_Email}/g, 'john@example.com');
    // preview = preview.replace(/{Last_Visit_Date}/g, 'Dec 15, 2023');

    // if (this.linkText) {
    //   preview += `\n\n${this.linkText}`;
    // }

    return preview;
  }

  // Validation methods
  validateSenderId(): void {
    if (!this.senderId || this.senderId.trim() === '') {
      this.senderIdError = 'Sender ID is required';
    } else if (this.senderId.length > this.maxSenderIdLength) {
      this.senderIdError = `Sender ID must not exceed ${this.maxSenderIdLength} characters`;
    } else {
      this.senderIdError = '';
    }
  }

  validateTextMessage(): void {
    if (!this.textMessage || this.textMessage.trim() === '') {
      this.textMessageError = 'Text message is required';
    } else if (this.textMessage.length > this.maxTextMessageLength) {
      this.textMessageError = `Text message must not exceed ${this.maxTextMessageLength} characters`;
    } else {
      this.textMessageError = '';
    }

    if (this.smsTextSegmentInfo.segments > this.maxTextSegments) {
      this.textMessageSegmentError = `Message exceeds ${this.maxTextSegments} segments. Please shorten your text.`;
    } else {
      this.textMessageSegmentError = '';
    }
  }

  validateLinkText(): void {
    const trimmed = this.linkText.trim();

    if (!trimmed) {
      this.linkTextError = '';
      return;
    }

    if (this.linkText.length > this.maxLinkTextLength) {
      this.linkTextError = `Link text must not exceed ${this.maxLinkTextLength} characters`;
      return;
    }

    if (!this.isValidUrl(trimmed)) {
      this.linkTextError = 'Please enter a valid URL';
      return;
    }

    this.linkTextError = '';
  }

  private isValidUrl(value: string): boolean {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  validateAllFields(): void {
    this.validateSenderId();
    this.validateTextMessage();
    this.validateLinkText();
  }

  isFormValid(): boolean {
    return (
      !this.senderIdError &&
      !this.textMessageError &&
      !this.textMessageSegmentError &&
      !this.linkTextError &&
      this.senderId.trim() !== '' &&
      this.textMessage.trim() !== ''
    );
  }

  // =====================
  // MMS Validation
  // =====================
  validateMmsImage(): void {
    this.mmsImageError = !this.mmsImagePreviewUrl && !this.mmsImageFile ? 'Image is required' : '';
  }

  validateMmsFallbackText(): void {
    this.mmsFallbackTextError =
      !this.mmsFallbackText || this.mmsFallbackText.trim() === ''
        ? 'Fallback text is required'
        : '';
    this.mmsFallbackSegmentError =
      this.getSegmentInfo(this.mmsFallbackText).segments > this.maxMmsSegments
        ? `Fallback text exceeds ${this.maxMmsSegments} segments. Please shorten your text.`
        : '';
  }

  onMmsFallbackTextBlur(): void {
    this.mmsFallbackTextTouched = true;
    this.validateMmsFallbackText();
  }

  onMmsFallbackTextChange(value: string): void {
    if (this.getSegmentInfo(value).segments > this.maxMmsSegments) {
      value = this.truncateToMaxSegments(value, this.maxMmsSegments);
    }
    this.mmsFallbackText = value;
    this.validateMmsFallbackText();
  }

  onMmsFallbackTextKeyDown(event: KeyboardEvent): void {
    if (this.blockKeydownAtSegmentLimit(event, this.mmsFallbackText, this.maxMmsSegments)) {
      this.mmsFallbackSegmentError = `Fallback text exceeds ${this.maxMmsSegments} segments. Please shorten your text.`;
    }
  }

  validateMmsCaption(): void {
    this.mmsCaptionSegmentError =
      this.getSegmentInfo(this.mmsCaption).segments > this.maxMmsSegments
        ? `Caption exceeds ${this.maxMmsSegments} segments. Please shorten your text.`
        : '';
  }

  onMmsCaptionChange(value: string): void {
    if (this.getSegmentInfo(value).segments > this.maxMmsSegments) {
      value = this.truncateToMaxSegments(value, this.maxMmsSegments);
    }
    this.mmsCaption = value;
    this.validateMmsCaption();
  }

  onMmsCaptionKeyDown(event: KeyboardEvent): void {
    if (this.blockKeydownAtSegmentLimit(event, this.mmsCaption, this.maxMmsSegments)) {
      this.mmsCaptionSegmentError = `Caption exceeds ${this.maxMmsSegments} segments. Please shorten your text.`;
    }
  }

  validateMmsBubble2Text(): void {
    this.mmsBubble2SegmentError =
      this.getSegmentInfo(this.mmsBubble2Text).segments > this.maxMmsSegments
        ? `Message exceeds ${this.maxMmsSegments} segments. Please shorten your text.`
        : '';
  }

  onMmsBubble2TextChange(value: string): void {
    if (this.getSegmentInfo(value).segments > this.maxMmsSegments) {
      value = this.truncateToMaxSegments(value, this.maxMmsSegments);
    }
    this.mmsBubble2Text = value;
    this.validateMmsBubble2Text();
  }

  onMmsBubble2TextKeyDown(event: KeyboardEvent): void {
    if (this.blockKeydownAtSegmentLimit(event, this.mmsBubble2Text, this.maxMmsSegments)) {
      this.mmsBubble2SegmentError = `Message exceeds ${this.maxMmsSegments} segments. Please shorten your text.`;
    }
  }

  private blockKeydownAtSegmentLimit(
    event: KeyboardEvent,
    currentText: string,
    maxSegments: number
  ): boolean {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab',
      'Enter',
    ];
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    if (allowedKeys.includes(event.key) || isCtrlOrCmd || event.key.length !== 1) {
      return false;
    }
    const textarea = event.target as HTMLTextAreaElement;
    const start = textarea.selectionStart ?? currentText.length;
    const end = textarea.selectionEnd ?? currentText.length;
    const projectedText = currentText.slice(0, start) + event.key + currentText.slice(end);
    if (this.getSegmentInfo(projectedText).segments > maxSegments) {
      event.preventDefault();
      return true;
    }
    return false;
  }

  validateAllMmsFields(): void {
    this.mmsImageTouched = true;
    this.validateMmsImage();
    this.mmsFallbackTextTouched = true;
    this.validateMmsFallbackText();
    this.validateMmsCaption();
    this.validateMmsBubble2Text();
    if (this.mmsUrl.trim()) {
      this.mmsUrlTouched = true;
      this.onMmsUrlInput();
    }
  }

  isMmsFormValid(): boolean {
    const imageOk = !!(this.mmsImageUrl || this.mmsImagePreviewUrl || this.mmsImageFile);
    const fallbackOk =
      this.mmsFallbackText.trim() !== '' &&
      !this.mmsFallbackTextError &&
      !this.mmsFallbackSegmentError;
    const captionOk = !this.mmsCaptionSegmentError;
    const bubble2Ok =
      !this.splitTwoBubbles || (this.mmsBubble2Text.trim() !== '' && !this.mmsBubble2SegmentError);
    const urlOk = !this.mmsUrl.trim() || this.mmsUrlValid;
    return imageOk && fallbackOk && captionOk && bubble2Ok && urlOk && !this.isMmsImageUploading;
  }

  // Blur event handlers
  onSenderIdBlur(): void {
    this.senderIdTouched = true;
    this.validateSenderId();
  }

  onTextMessageBlur(): void {
    this.textMessageTouched = true;
    this.validateTextMessage();
    // Save cursor position before losing focus
    this.saveCursorPosition();
  }

  onLinkTextBlur(): void {
    this.linkTextTouched = true;
    this.validateLinkText();
  }

  onSenderIdInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value.length > this.maxSenderIdLength) {
      input.value = input.value.substring(0, this.maxSenderIdLength);
      this.senderId = input.value;
    }
  }

  onLinkTextInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value.length > this.maxLinkTextLength) {
      input.value = input.value.substring(0, this.maxLinkTextLength);
      this.linkText = input.value;
    }
    this.linkTextTouched = true;
    this.validateLinkText();
  }

  hasCurlyBraces(): boolean {
    return Boolean(this.textMessage && /\{\{[^}]*\}\}/.test(this.textMessage));
  }

  ngAfterViewChecked(): void {
    // Update formatted content if textMessage changed externally (not from user typing)
    if (this.textMessage !== this.lastTextMessage && !this.isUpdatingContent && this.textAreaRef) {
      this.updateFormattedContent();
      this.lastTextMessage = this.textMessage;
    }
  }

  /**
   * Update and reformat the editable text area content while preserving the cursor position and updating component state.
   * @example
   * pdateFormattedContent()
   * undefined
   * @returns {void} No return value.
   **/
  updateFormattedContent(): void {
    if (!this.textAreaRef) return;

    const element = this.textAreaRef.nativeElement;
    const selection = window.getSelection();
    let cursorPosition = 0;

    // Save cursor position
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      cursorPosition = preCaretRange.toString().length;
    }

    this.applyTextFormatting(element);

    if (this.textMessage) {
      // Restore cursor position
      this.restoreCursorPosition(element, cursorPosition);
    }

    this.isUpdatingContent = false;
    this.lastTextMessage = this.textMessage;
  }

  /**
   * Sets isUpdatingContent, then writes escaped and curly-brace-highlighted HTML into element.innerHTML.
   * Shared by insertTag() (no cursor restore) and updateFormattedContent() (restores cursor after).
   */
  private applyTextFormatting(element: HTMLElement): void {
    this.isUpdatingContent = true;
    if (!this.textMessage) {
      element.innerHTML = '';
    } else {
      const escaped = this.textMessage
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const formatted = escaped.replace(
        /\{\{([^}]+)\}\}/g,
        '<span class="curly-brace-text">{{$1}}</span>'
      );
      element.innerHTML = formatted;
    }
  }

  restoreCursorPosition(element: HTMLElement, position: number): void {
    this.setSelectionAtPosition(element, position);
  }

  /**
   * Handle input events on the editable text element: enforce max length, preserve cursor, and update component text state and formatted content.
   * @example
   * nTextInput(event)
   * undefined
   * @param {Event} event - The DOM Event from the editable/contenteditable element triggering the input.
   * @returns {void} Does not return a value.
   **/
  onTextInput(event: Event): void {
    if (this.isUpdatingContent) return;

    const element = event.target as HTMLElement;
    let newText = element.textContent || '';

    // Enforce character limit
    if (newText.length > this.maxTextMessageLength) {
      newText = newText.substring(0, this.maxTextMessageLength);
      element.textContent = newText;
      // Restore cursor position after truncating
      setTimeout(() => {
        this.setCursorPosition(Math.min(this.savedCursorPosition, this.maxTextMessageLength));
      }, 0);
    }

    // Block/roll back typing beyond the max segment limit
    if (this.getSegmentInfo(newText).segments > this.maxTextSegments) {
      newText = this.truncateToMaxSegments(newText);
      element.textContent = newText;
      setTimeout(() => {
        this.setCursorPosition(Math.min(this.savedCursorPosition, newText.length));
      }, 0);
    }

    // Save cursor position
    this.saveCursorPosition();

    // Only update if text actually changed to prevent infinite loops
    if (newText !== this.textMessage) {
      this.textMessage = newText;
      this.lastTextMessage = newText;
      this.validateTextMessage();
      // Update formatted content after a short delay to allow typing to complete
      setTimeout(() => {
        if (this.textMessage === newText) {
          this.updateFormattedContent();
        }
      }, 100);
    }
  }

  /**
   * Save the current cursor/caret character offset inside the component's editable text area to this.savedCursorPosition.
   * @example
   * saveCursorPosition()
   * undefined
   * @returns {void} Saves the caret position; does not return a value.
   **/
  private saveCursorPosition(): void {
    if (!this.textAreaRef) return;

    const element = this.textAreaRef.nativeElement;
    const selection = window.getSelection();

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      // Check if range is within our textarea element
      if (
        element.contains(range.commonAncestorContainer) ||
        element === range.commonAncestorContainer
      ) {
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        this.savedCursorPosition = preCaretRange.toString().length;
      }
    }
  }

  onTextAreaFocus(): void {
    // Save cursor position when focusing
    setTimeout(() => this.saveCursorPosition(), 0);
  }

  onTextAreaClick(): void {
    // Save cursor position when clicking
    setTimeout(() => this.saveCursorPosition(), 0);
  }

  /**
   * Handle keydown on the text area: save cursor position and block input when at max length except for navigation, deletion, and modifier-key shortcuts.
   * @example
   * nTextAreaKeyDown(event)
   * undefined
   * @param {KeyboardEvent} event - KeyboardEvent from the textarea keydown.
   * @returns {void} Does not return a value.
   **/
  onTextAreaKeyDown(event: KeyboardEvent): void {
    // Save cursor position on keydown (before input)
    setTimeout(() => this.saveCursorPosition(), 0);

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab',
      'Enter',
    ];
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    const isAllowedKey = allowedKeys.includes(event.key) || isCtrlOrCmd;

    if (isAllowedKey) return;

    // Prevent typing if at character limit (allow backspace, delete, arrow keys, etc.)
    if (this.textMessage.length >= this.maxTextMessageLength) {
      event.preventDefault();
      return;
    }

    // Prevent typing a character that would push the message past the max segment limit
    if (event.key.length === 1) {
      const cursor = Math.max(0, Math.min(this.savedCursorPosition, this.textMessage.length));
      const projectedText =
        this.textMessage.slice(0, cursor) + event.key + this.textMessage.slice(cursor);
      if (this.getSegmentInfo(projectedText).segments > this.maxTextSegments) {
        event.preventDefault();
        this.textMessageSegmentError = `Message exceeds ${this.maxTextSegments} segments. Please shorten your text.`;
      }
    }
  }

  /**
   * Handle paste events into the editable text message area: prevents default paste behavior, inserts plain text truncated to the remaining allowed characters, updates the selection, and refreshes the component model and formatted content.
   * @example
   * nTextPaste(event)
   * void
   * @param {{ClipboardEvent}} {{event}} - The clipboard paste event from the editable element.
   * @returns {{void}} Updates this.textMessage and this.lastTextMessage and schedules updateFormattedContent(); no value is returned.
   **/
  onTextPaste(event: ClipboardEvent): void {
    event.preventDefault();
    let text = event.clipboardData?.getData('text/plain') || '';

    // Calculate how much text can be pasted
    const currentLength = this.textMessage.length;
    const remainingChars = this.maxTextMessageLength - currentLength;

    if (text.length > remainingChars) {
      text = text.substring(0, remainingChars);
    }

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    // Update model
    const element = event.target as HTMLElement;
    let finalText = element.textContent || '';

    // Block pasted content beyond the max segment limit
    if (this.getSegmentInfo(finalText).segments > this.maxTextSegments) {
      finalText = this.truncateToMaxSegments(finalText);
      element.textContent = finalText;
      setTimeout(() => {
        this.setCursorPosition(Math.min(this.savedCursorPosition, finalText.length));
      }, 0);
    }

    this.textMessage = finalText;
    this.lastTextMessage = this.textMessage;
    this.validateTextMessage();
    setTimeout(() => this.updateFormattedContent(), 0);
  }

  // =====================
  // MMS methods
  // =====================
  setMessageType(type: 'SMS' | 'MMS'): void {
    this.messageType = type;
    if (type === 'SMS') {
      // ngIf destroys and recreates the contenteditable element when switching back;
      // reset lastTextMessage so ngAfterViewChecked re-renders the formatted content.
      this.lastTextMessage = '';
      setTimeout(() => this.updateFormattedContent(), 0);
    }
  }

  onMmsUrlInput(): void {
    const trimmed = this.mmsUrl.trim();
    if (!trimmed) {
      this.mmsUrlValid = false;
      this.mmsUrlError = '';
      return;
    }
    this.mmsUrlValid = this.isValidUrl(trimmed);
    this.mmsUrlError = this.mmsUrlValid ? '' : 'Please enter a valid URL';
  }

  onMmsUrlBlur(): void {
    this.mmsUrlTouched = true;
    this.onMmsUrlInput();
  }

  onMmsFileDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onMmsFileDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (file) this.handleMmsFile(file);
  }

  onMmsFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.handleMmsFile(file);
  }

  handleMmsFile(file: File): void {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.mmsImageError = 'File size must be 5 MB or less';
      this.mmsImageTouched = true;
      return;
    }
    this.mmsImageFile = file;
    const reader = new FileReader();
    reader.onload = e => {
      this.mmsImagePreviewUrl = e.target?.result as string;
      this.mmsImageError = '';
      this.mmsImageTouched = false;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);

    this.isMmsImageUploading = true;
    this.readImageDimensions(file).then(dims => {
      if (dims) {
        this.mmsImageWidth = dims.width;
        this.mmsImageHeight = dims.height;
      }
    });

    const formData = new FormData();
    appendMmsCouponImageToFormData(formData, file);
    this.apiService.uploadCampaignFile(formData).subscribe({
      next: response => {
        const url =
          extractCampaignLogoUploadUrl(response, MMS_COUPON_IMAGE_FIELD) ||
          extractCampaignLogoUploadUrl(response, 'mms_coupon_image');
        if (url) {
          this.mmsImageUrl = url;
          this.mmsImageError = '';
        } else {
          this.mmsImageError = 'Image upload failed. Please try again.';
          this.mmsImageUrl = '';
        }
        this.isMmsImageUploading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.mmsImageError = 'Image upload failed. Please try again.';
        this.mmsImageUrl = '';
        this.isMmsImageUploading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  insertMmsCaptionTag(tag: string): void {
    this.onMmsCaptionChange(this.mmsCaption + `{{${tag}}} `);
  }

  insertMmsFallbackTag(tag: string): void {
    this.onMmsFallbackTextChange(this.mmsFallbackText + `{{${tag}}} `);
  }

  insertMmsBubble2Tag(tag: string): void {
    this.onMmsBubble2TextChange(this.mmsBubble2Text + `{{${tag}}} `);
  }

  onMmsReset(): void {
    this.resetMmsFields();
  }

  // =====================
  // Segment calculator
  // =====================
  private truncateToMaxSegments(text: string, maxSegments: number = this.maxTextSegments): string {
    let truncated = text;
    while (truncated.length > 0 && this.getSegmentInfo(truncated).segments > maxSegments) {
      truncated = truncated.substring(0, truncated.length - 1);
    }
    return truncated;
  }

  getSegmentInfo(text: string): { segments: number; encoding: string; chars: number } {
    const chars = text ? text.length : 0;
    if (!text || !text.trim()) {
      return { segments: 0, encoding: 'GSM-7', chars };
    }
    try {
      if (typeof SegmentedMessage !== 'undefined') {
        const msg = new SegmentedMessage(text);
        return { segments: msg.segmentsCount, encoding: msg.encodingName, chars };
      }
    } catch (_) {
      /* fallback below */
    }
    return { segments: 1, encoding: 'GSM-7', chars };
  }

  get smsTextSegmentInfo() {
    return this.getSegmentInfo(this.textMessage);
  }
  get mmsCaptionSegmentInfo() {
    return this.getSegmentInfo(this.mmsCaption);
  }

  // =====================
  // SMS cost calculation
  // =====================
  get smsTotalSegmentCount(): number {
    if (!this.textMessage.trim() && !this.linkText.trim()) return 0;
    return Math.max(1, this.smsSegmentParts.length);
  }

  get smsTotalCost(): number {
    return this.smsTotalSegmentCount * 0.0083;
  }

  // =====================
  // MMS cost calculation
  // =====================
  get mmsCaptionSegmentCount(): number {
    // Image always counts as at least 1 MMS even without caption
    return Math.max(1, this.mmsCaptionSegmentParts.length);
  }

  get mmsBubble2SegmentCount(): number {
    if (!this.splitTwoBubbles || !this.mmsBubble2Text.trim()) return 0;
    return Math.max(1, this.mmsBubble2SegmentParts.length);
  }

  get mmsFallbackSegmentCount(): number {
    if (!this.mmsFallbackText.trim()) return 0;
    return Math.max(1, this.mmsFallbackSegmentInfo.segments);
  }

  get mmsCaptionCost(): number {
    return this.mmsCaptionSegmentCount * 0.022;
  }

  get mmsBubble2Cost(): number {
    return this.mmsBubble2SegmentCount * 0.0083;
  }

  get mmsTotalCost(): number {
    return this.mmsCaptionCost + this.mmsBubble2Cost;
  }

  get mmsFallbackCost(): number {
    return this.mmsFallbackSegmentCount * 0.0083;
  }

  formatCost(value: number): string {
    return value.toFixed(4).replace(/\.?0+$/, '') || '0';
  }
  get mmsFallbackSegmentInfo() {
    return this.getSegmentInfo(this.mmsFallbackText);
  }
  get mmsBubble2SegmentInfo() {
    return this.getSegmentInfo(this.mmsBubble2Text);
  }

  private buildSegmentParts(text: string): string[] {
    if (!text || !text.trim()) return [''];
    try {
      if (typeof SegmentedMessage !== 'undefined') {
        const msg = new SegmentedMessage(text);
        if (msg.segmentsCount <= 1) return [text];
        return (msg.segments as any[][]).map((seg: any[]) => seg.map((c: any) => c.raw).join(''));
      }
    } catch {
      /* ignore */
    }
    return [text];
  }

  get smsSegmentParts(): string[] {
    const full = this.textMessage + (this.linkText.trim() ? '\n\n' + this.linkText : '');
    return this.buildSegmentParts(full);
  }

  get mmsCaptionSegmentParts(): string[] {
    return this.buildSegmentParts(this.mmsCaption);
  }

  get mmsBubble2SegmentParts(): string[] {
    return this.buildSegmentParts(this.mmsBubble2Text);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
